'use client';

import React, { useState, useRef, useEffect, Suspense } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface Branch {
  city: string;
  address: string;
  phones: string[];
  email: string;
  hours: string;
  isHQ?: boolean;
  type?: string;
  searchAliases?: string[];
}

interface Country {
  name: string;
  flag: string;
  code: string;
  branches: Branch[];
}

const countries: Country[] = [
  {
    name: 'Bulgaria',
    flag: '🇧🇬',
    code: 'BG',
    branches: [
      {
        city: 'Sofia (Head Office)',
        address: '1517 Sofia, Bulgaria — WTO Bulgaria Logistics Center, 126 Mimi Balkanska str. (in front of VIP terminal, Terminal 1, Sofia Airport)',
        phones: [
          '+359 2 858 1930',
          'Sales: +359 2 858 1930',
          'FCL Export: +359 2 958 1531',
          'FCL Import: +359 2 858 1940',
          'LCL: +359 2 858 6150',
        ],
        email: 'info@wto.com | sales.sofia@wto.com',
        hours: 'Mon–Fri: 08:30–17:30',
        isHQ: true,
        type: 'Headquarters',
      },
      {
        city: 'Varna',
        address: 'St. Brothers Shkorpil 26A, fl. 5, office 23, 9000 Varna, Bulgaria',
        phones: ['+359 52 648 693', '+359 52 601 806', '+359 52 601 533'],
        email: 'sales.varna@wto.com',
        hours: 'Mon–Fri: 08:30–17:30',
        type: 'Branch Office',
      },
      {
        city: 'Plovdiv',
        address: '1, Khan Kubrat str., office 405, 4000 Plovdiv, Bulgaria',
        phones: ['+359 32 275485', '+359 32 275486', '+359 32 275487'],
        email: 'sales.plovdiv@wto.com',
        hours: 'Mon–Fri: 08:30–17:30',
        type: 'Branch Office',
      },
      {
        city: 'Burgas',
        address: 'Tsar Simeon I str., no. 71, floor 3, office 6, 8000 Burgas, Bulgaria',
        phones: ['+359 56 820116'],
        email: 'sales.burgas@wto.com',
        hours: 'Mon–Fri: 08:30–17:30',
        type: 'Branch Office',
      },
      {
        city: 'Ruse',
        address: '32 Tsar Ferdinand blvd., floor 2, 7000 Ruse, Bulgaria',
        phones: ['+359 82 280200', '+359 82 280222'],
        email: 'sales.ruse@wto.com',
        hours: 'Mon–Fri: 08:30–17:30',
        type: 'Branch Office',
      },
    ],
  },
  {
    name: 'Bosnia and Herzegovina',
    flag: '🇧🇦',
    code: 'BA',
    branches: [
      {
        city: 'Sarajevo',
        address: 'Ul. Dr. Mustafe Pintola do br. 23/2, 71240 Ilidža, Bosnia and Herzegovina',
        phones: ['+387 61 281 354'],
        email: 'sales.ba@wto.com | vladan.kuharevic@wto.com',
        hours: 'Mon–Fri: 09:00–17:00',
        type: 'Country Office',
      },
    ],
  },
  {
    name: 'China',
    flag: '🇨🇳',
    code: 'CN',
    branches: [
      {
        city: 'Shanghai',
        address: 'Office 1008, Longsheng Business Building, Block B, Lane 777 Wanrong Road, Shanghai 200072, China',
        phones: ['+86 21 60718300'],
        email: 'sales.sha@cn.wtogroup.com',
        hours: 'Mon–Fri: 09:00–18:00',
        type: 'Country Office',
      },
      {
        city: 'Ningbo',
        address: 'No.609 B Building, No.565 Jingjia Road, Yinzhou District, Ningbo, Zhejiang Province, China',
        phones: ['+86 153 8188 0509'],
        email: 'management.nbo@cn.wtogroup.com',
        hours: 'Mon–Fri: 09:00–18:00',
        type: 'Branch Office',
      },
      {
        city: 'Chongqing',
        address: '24th Fl, Building A, Jiazhou Xiexin Center, 57 Hongjin Av., Yubei District, Chongqing, China',
        phones: ['+86 177 2328 5037'],
        email: 'sales.ckg@cn.wtogroup.com',
        hours: 'Mon–Fri: 09:00–18:00',
        type: 'Branch Office',
      },
      {
        city: 'Shenzhen',
        address: 'Room A008, 16th floor, Block C, Dongfang Xintiandi Plaza, Caitian Road, Futian District, Shenzhen, China',
        phones: ['+86 574 87280910'],
        email: 'doc1.szx@cn.wtogroup.com',
        hours: 'Mon–Fri: 09:00–18:00',
        type: 'Branch Office',
      },
    ],
  },
  {
    name: 'Croatia',
    flag: '🇭🇷',
    code: 'HR',
    branches: [
      {
        city: 'Zagreb',
        address: 'Radnicka cesta 30 str., 10 000 Zagreb, Croatia',
        phones: ['+385 1 618 77 52', '+385 91 4109144'],
        email: 'sales.hr@wto.com',
        hours: 'Mon–Fri: 09:00–17:00',
        type: 'Country Office',
      },
    ],
  },
  {
    name: 'Germany',
    flag: '🇩🇪',
    code: 'DE',
    branches: [
      {
        city: 'Hamburg',
        address: 'Indiastrasse 3, 20457 Hamburg, Germany',
        phones: ['+49 178 4673754'],
        email: 'sales.de@wto.com',
        hours: 'Mon–Fri: 08:00–17:00',
        type: 'Country Office',
        searchAliases: [
          'Berlin', 'Munich', 'Frankfurt', 'Cologne', 'Stuttgart', 'Düsseldorf',
          'Dusseldorf', 'Leipzig', 'Dortmund', 'Essen', 'Bremen', 'Dresden',
          'Hanover', 'Hannover', 'Nuremberg', 'Nürnberg', 'Duisburg', 'Bochum',
          'Wuppertal', 'Bielefeld', 'Bonn', 'Mannheim', 'Karlsruhe', 'Augsburg',
          'Wiesbaden', 'Münster', 'Munster', 'Gelsenkirchen', 'Aachen', 'Mönchengladbach',
          'Braunschweig', 'Kiel', 'Chemnitz', 'Magdeburg', 'Halle', 'Freiburg',
          'Krefeld', 'Lübeck', 'Lubeck', 'Oberhausen', 'Erfurt', 'Mainz',
          'Rostock', 'Kassel', 'Hagen', 'Hamm', 'Saarbrücken', 'Saarbrucken',
          'Mülheim', 'Mulheim', 'Potsdam', 'Leverkusen', 'Oldenburg', 'Osnabrück',
          'Osnabrück', 'Heidelberg', 'Darmstadt', 'Regensburg', 'Ingolstadt',
          'Würzburg', 'Wurzburg', 'Ulm', 'Fürth', 'Furth', 'Wolfsburg',
          'Heilbronn', 'Pforzheim', 'Göttingen', 'Gottingen', 'Bottrop',
          'Recklinghausen', 'Reutlingen', 'Bremerhaven', 'Koblenz', 'Erlangen',
          'Moers', 'Siegen', 'Hildesheim', 'Trier',
        ],
      },
    ],
  },
  {
    name: 'Greece',
    flag: '🇬🇷',
    code: 'GR',
    branches: [
      {
        city: 'Thessaloniki',
        address: '7 Karatassou street, floor 5, 54626 Thessaloniki, Greece',
        phones: ['+30 2310 514444'],
        email: 'maria.georgiou@wto.com | sales.gr@wto.com',
        hours: 'Mon–Fri: 09:00–17:00',
        type: 'Branch Office',
      },
      {
        city: 'Piraeus',
        address: '111 Filonos Street, 7th floor, 18545 Piraeus, Greece',
        phones: ['+30 210 4611300', '+30 6970004286'],
        email: 'stavros.kanapitsas@wto.com | sales.gr@wto.com',
        hours: 'Mon–Fri: 09:00–17:00',
        type: 'Branch Office',
      },
    ],
  },
  {
    name: 'Kosovo',
    flag: '🇽🇰',
    code: 'XK',
    branches: [
      {
        city: 'Pristina',
        address: 'Str. Tirana, Icon Tower, S3, 8th Floor, Pristina 10000, Kosovo',
        phones: ['+383 49 889 439'],
        email: 'herolind.xhemajliu@wto.com | sales.xk@wto.com',
        hours: 'Mon–Fri: 09:00–17:00',
        type: 'Country Office',
      },
    ],
  },
  {
    name: 'North Macedonia',
    flag: '🇲🇰',
    code: 'MK',
    branches: [
      {
        city: 'Skopje',
        address: 'Ul.1550 Br.20 / 2ri kat, Skopje 1000, N. Macedonia',
        phones: ['+389 78 208 664'],
        email: 'sales.mk@wto.com | dragan.janev@wto.com',
        hours: 'Mon–Fri: 09:00–17:00',
        type: 'Country Office',
      },
    ],
  },
  {
    name: 'Montenegro',
    flag: '🇲🇪',
    code: 'ME',
    branches: [
      {
        city: 'Podgorica',
        address: 'Milana Popovica No.7, Podgorica 81000, Montenegro',
        phones: ['+382 68 188388'],
        email: 'sales.me@wto.com',
        hours: 'Mon–Fri: 09:00–17:00',
        type: 'Country Office',
      },
    ],
  },
  {
    name: 'Romania',
    flag: '🇷🇴',
    code: 'RO',
    branches: [
      {
        city: 'Bucharest',
        address: 'Caderea Bastiliei, No 64, Floor No 5, Sector 1, 010616, Bucharest, Romania',
        phones: ['+40 722 882414'],
        email: 'info.ro@wto.com',
        hours: 'Mon–Fri: 09:00–18:00',
        type: 'Country Office',
      },
    ],
  },
  {
    name: 'Serbia',
    flag: '🇷🇸',
    code: 'RS',
    branches: [
      {
        city: 'Belgrade',
        address: 'Trešnjinog cveta 1b, 11000 Belgrade, Serbia',
        phones: ['+381 62 470787', '+381 11 7850 254', '+381 11 7850 255', '+381 11 7850 256'],
        email: 'sales.rs@wto.com | bogdan.gavrilovic@wto.com',
        hours: 'Mon–Fri: 09:00–17:00',
        type: 'Country Office',
      },
    ],
  },
  {
    name: 'Slovenia',
    flag: '🇸🇮',
    code: 'SI',
    branches: [
      {
        city: 'Koper',
        address: 'Ankaranska 12, 6000 Koper, Slovenia',
        phones: ['+386 81601058'],
        email: 'sales.si@wto.com',
        hours: 'Mon–Fri: 09:00–17:00',
        type: 'Branch Office',
      },
      {
        city: 'Ljubljana',
        address: 'Dolenjska cesta 242, 1000 Ljubljana, Slovenia',
        phones: ['+386 40 195575'],
        email: 'sales.si@wto.com',
        hours: 'Mon–Fri: 09:00–17:00',
        type: 'Branch Office',
      },
    ],
  },
  {
    name: 'Taiwan',
    flag: '🇹🇼',
    code: 'TW',
    branches: [
      {
        city: 'Taipei',
        address: 'Rm. 2, 11F., No. 57, Fuxing N. Rd., Songshan Dist., 105404, Taipei City, Taiwan (R.O.C.)',
        phones: ['+886-2-2777-5775'],
        email: 'sales@tw.wtogroup.com',
        hours: 'Mon–Fri: 09:00–18:00',
        type: 'Country Office',
      },
    ],
  },
  {
    name: 'USA',
    flag: '🇺🇸',
    code: 'US',
    branches: [
      {
        city: 'Philadelphia',
        address: '3748 West Chester Pike, Suite 103, 1st Floor, Newtown Square, PA 19073, USA',
        phones: ['+1 484 494 1177'],
        email: 'sales.us@wto.com | operations.us@wto.com',
        hours: 'Mon–Fri: 09:00–17:00 EST',
        type: 'Branch Office',
      },
      {
        city: 'Chicago',
        address: '3400 W Stonegate Blvd, Suite #101, Arlington Heights, Illinois 60005, USA',
        phones: ['+1 773 937 7005'],
        email: 'sales.us@wto.com | operations.us@wto.com',
        hours: 'Mon–Fri: 09:00–17:00 CST',
        type: 'Branch Office',
      },
      {
        city: 'New York',
        address: 'World Transport Overseas NY, Pearl River Office Evolution, 1 Blue Hill Plaza, Lobby Level, #1509, Pearl River, NY 10965, USA',
        phones: ['+1 848 565 1112'],
        email: 'sales.us@wto.com | operations.us@wto.com',
        hours: 'Mon–Fri: 09:00–17:00 EST',
        type: 'Branch Office',
        searchAliases: ['Pearl River', 'Blue Hill Plaza', 'World Transport Overseas NY'],
      },
    ],
  },
  {
    name: 'Vietnam',
    flag: '🇻🇳',
    code: 'VN',
    branches: [
      {
        city: 'Ho Chi Minh City',
        address: '2nd Floor, Ha Do Airport Building, 02 Hong Ha street, Tan Son Hoa ward, Ho Chi Minh City, Vietnam',
        phones: ['+84 765 823 461'],
        email: 'sales.vn@wto.com | khaing.thin@wto.com',
        hours: 'Mon–Fri: 08:00–17:00',
        type: 'Country Office',
      },
    ],
  },
  {
    name: 'Albania',
    flag: '🇦🇱',
    code: 'AL',
    branches: [
      {
        city: 'Tirana',
        address: 'Str. Emin Duraku (Pallati Credins), 7th Floor Ap. 7/2, Tirana 1019, Albania',
        phones: ['+355 69 20 50 410'],
        email: 'ledio.saliko@wto.com | sales.al@wto.com',
        hours: 'Mon–Fri: 09:00–17:00',
        type: 'Country Office',
      },
    ],
  },
];

const contactReasons = [
  'General Inquiry',
  'Request a Quote',
  'Container Transport',
  'LCL Transport',
  'Airfreight',
  'Inland Transport',
  'Customs Representation',
  'Port Operations',
  'Cargo Insurance',
  'Bank Guarantee',
  'Other',
];

interface FormState {
  name: string;
  company: string;
  email: string;
  phone: string;
  reason: string;
  message: string;
}

// Icons
const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.18 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 5.55 5.55l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);
const EmailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);
const PinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);
const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    className="w-5 h-5 transition-transform duration-300"
    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

function BranchCard({ branch }: { branch: Branch }) {
  return (
    <div className="rounded-xl p-5 transition-all duration-200"
      style={{ background: '#FFFFFF', border: '1px solid rgba(15,35,64,0.1)' }}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-base font-bold" style={{ color: '#0F2340' }}>{branch.city}</h4>
            {branch.isHQ && (
              <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(26,95,171,0.12)', border: '1px solid rgba(26,95,171,0.25)', color: '#1A5FAB' }}>HQ</span>
            )}
          </div>
          {branch.type && (
            <span className="text-xs" style={{ color: '#6A85A0' }}>{branch.type}</span>
          )}
        </div>
      </div>
      <div className="space-y-2.5">
        <div className="flex items-start gap-2.5">
          <span style={{ color: '#1B6B8A', marginTop: '1px', flexShrink: 0 }}><PinIcon /></span>
          <p className="text-xs leading-relaxed" style={{ color: '#3A5070' }}>{branch.address}</p>
        </div>
        <div className="flex items-start gap-2.5">
          <span style={{ color: '#1B6B8A', marginTop: '2px', flexShrink: 0 }}><PhoneIcon /></span>
          <div className="flex flex-col gap-1">
            {branch.phones.map((num, idx) => (
              <a
                key={idx}
                href={`tel:${num.replace(/[^\d+]/g, '')}`}
                className="text-xs font-medium transition-colors leading-snug"
                style={{ color: '#3A5070' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#1B6B8A')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#3A5070')}
              >
                {num}
              </a>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <span style={{ color: '#1B6B8A', flexShrink: 0 }}><EmailIcon /></span>
          <a href={`mailto:${branch.email.split(' | ')[0]}`} className="text-xs font-medium transition-colors"
            style={{ color: '#3A5070' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#1B6B8A')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#3A5070')}>
            {branch.email}
          </a>
        </div>
        <div className="flex items-center gap-2.5">
          <span style={{ color: '#1B6B8A', flexShrink: 0 }}><ClockIcon /></span>
          <p className="text-xs" style={{ color: '#3A5070' }}>{branch.hours}</p>
        </div>
      </div>
    </div>
  );
}

function CountryAccordion({ country, isOpen, onToggle, searchQuery, accordionRef }: {
  country: Country;
  isOpen: boolean;
  onToggle: () => void;
  searchQuery: string;
  accordionRef: React.RefObject<HTMLDivElement | null>;
}) {
  const q = searchQuery.toLowerCase();
  const filteredBranches = searchQuery
    ? country.branches.filter(b =>
        b.city.toLowerCase().includes(q) ||
        b.address.toLowerCase().includes(q) ||
        b.email.toLowerCase().includes(q) ||
        (b.searchAliases && b.searchAliases.some(a => a.toLowerCase().includes(q)))
      )
    : country.branches;

  if (searchQuery && filteredBranches.length === 0) return null;

  return (
    <div ref={accordionRef} className="rounded-2xl overflow-hidden transition-all duration-200"
      style={{ border: isOpen ? '1px solid rgba(26,95,171,0.3)' : '1px solid rgba(15,35,64,0.1)', background: isOpen ? 'rgba(26,95,171,0.05)' : '#FFFFFF' }}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-5 text-left transition-all duration-200"
        style={{ background: 'transparent' }}
        onMouseEnter={(e) => { if (!isOpen) e.currentTarget.style.background = 'rgba(15,35,64,0.03)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2.5">
            <span
              className="text-xs font-bold px-2 py-0.5 rounded"
              style={{
                background: '#1B6B8A',
                color: '#ffffff',
                letterSpacing: '0.05em',
                border: '1px solid #1B6B8A',
              }}
            >
              {country.code}
            </span>
            <div>
              <h3 className="text-lg font-bold" style={{ color: '#0F2340' }}>{country.name}</h3>
              <p className="text-xs mt-0.5" style={{ color: '#6A85A0' }}>
                {country.branches.length} {country.branches.length === 1 ? 'office' : 'offices'}
                {country.branches.some(b => b.isHQ) && <span className="ml-2 px-1.5 py-0.5 rounded text-xs" style={{ background: 'rgba(26,95,171,0.12)', color: '#1A5FAB' }}>Includes HQ</span>}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!isOpen && (
            <div className="hidden sm:flex flex-wrap gap-1.5">
              {country.branches.slice(0, 3).map((b) => (
                <span key={b.city} className="text-xs px-2.5 py-1 rounded-full" style={{ background: 'rgba(15,35,64,0.07)', color: '#3A5070' }}>{b.city}</span>
              ))}
              {country.branches.length > 3 && (
                <span className="text-xs px-2.5 py-1 rounded-full" style={{ background: 'rgba(15,35,64,0.07)', color: '#6A85A0' }}>+{country.branches.length - 3} more</span>
              )}
            </div>
          )}
          <span style={{ color: isOpen ? '#1A5FAB' : '#6A85A0' }}>
            <ChevronIcon open={isOpen} />
          </span>
        </div>
      </button>

      {isOpen && (
        <div className="px-6 pb-6">
          <div className="w-full h-px mb-5" style={{ background: 'rgba(15,35,64,0.1)' }} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredBranches.map((branch) => (
              <BranchCard key={branch.city} branch={branch} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ContactsPage() {
  return (
    <Suspense fallback={null}>
      <ContactsPageInner />
    </Suspense>
  );
}
function ContactsPageInner() {
  const [openCountry, setOpenCountry] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({ name: '', company: '', email: '', phone: '', reason: '', message: '' });
  const [errors, setErrors] = useState<Partial<FormState>>({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formStartedAt] = useState(() => Date.now());
  const searchParams = useSearchParams();

  // Compute total offices across all countries
  const totalOffices = countries.reduce((sum, c) => sum + c.branches.length, 0);

  // One ref per country for scroll-to behaviour
  const accordionRefs = useRef<Record<string, React.RefObject<HTMLDivElement | null>>>(
    Object.fromEntries(countries.map(c => [c.name, React.createRef<HTMLDivElement>()]))
  );

  // Auto-open and scroll to country from URL query param
  useEffect(() => {
    const countryParam = searchParams.get('country');
    if (countryParam) {
      const matched = countries.find(c => c.name.toLowerCase() === countryParam.toLowerCase());
      if (matched) {
        setOpenCountry(matched.name);
        setTimeout(() => {
          const ref = accordionRefs.current[matched.name];
          if (ref?.current) {
            ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      }
    }
  }, [searchParams]);

  const update = (field: keyof FormState, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Partial<FormState> = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (!form.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Enter a valid email';
    if (!form.message.trim()) newErrors.message = 'Message is required';
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    setSending(true);
    setSubmitError('');
    try {
      const res = await fetch('/mail.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          formType: 'contact',
          website: '',
          formStartedAt,
        }),
      });
      const responseText = await res.text();
      let data: { error?: string } = {};
      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch {
        throw new Error(responseText || 'Empty response from mail server.');
      }
      if (!res.ok) {
        throw new Error(data.error || responseText || 'Message could not be sent. Please try again.');
      }
      setSubmitted(true);
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Message could not be sent. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Open country, scroll to its accordion centered
  const handleCountryPillClick = (countryName: string) => {
    const isOpening = openCountry !== countryName;
    setOpenCountry(isOpening ? countryName : null);
    if (isOpening) {
      setTimeout(() => {
        const ref = accordionRefs.current[countryName];
        if (ref?.current) {
          ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 50);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#F4F6F9' }}>
      <Header />

      <main className="flex-1 pt-28">
        {/* ── Hero Banner ── */}
        <section className="relative overflow-hidden py-16 lg:py-20" style={{ background: 'linear-gradient(135deg, #0A1535 0%, #0D1B4B 50%, #112060 100%)' }}>
          <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(26,95,171,0.18) 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(96,165,250,0.1) 0%, transparent 70%)', transform: 'translate(-30%, 30%)' }} />

          <div className="relative max-w-6xl mx-auto px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 text-xs font-semibold uppercase tracking-widest" style={{ background: 'rgba(26,95,171,0.25)', border: '1px solid rgba(96,165,250,0.3)', color: '#7EB8F7' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
              {countries.length} Countries · {totalOffices} Offices Worldwide
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold mb-5 leading-tight" style={{ color: '#FFFFFF', fontFamily: 'var(--font-display, sans-serif)' }}>
              Our Global Offices
            </h1>
            <p className="text-lg lg:text-xl max-w-2xl mx-auto leading-relaxed mb-8" style={{ color: '#C8D8F0' }}>
              Find your nearest WTO office. Select a country to see all branches with full contact details.
            </p>

            {/* Quick scroll to contact form */}
            <a
              href="#contact-form"
              className="inline-flex items-center gap-2 text-sm font-medium transition-colors"
              style={{ color: '#7EB8F7' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#FFFFFF')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#7EB8F7')}
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById('contact-form');
                if (el) {
                  const headerOffset = 80;
                  const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
                  window.scrollTo({ top, behavior: 'smooth' });
                }
              }}
            >
              Send us a message
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M12 5v14M5 12l7 7 7-7" /></svg>
            </a>
          </div>
        </section>

        {/* ── Countries Accordion — Find an Office Near You ── */}
        <section id="find-office" className="py-16" style={{ background: '#F4F6F9' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="mb-10">
              <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
              <h2 className="text-3xl lg:text-4xl font-bold mb-3" style={{ color: '#0F2340' }}>
                Find an Office Near You
              </h2>
              <p className="text-base" style={{ color: '#3A5070' }}>
                Click on a country to expand and see all offices with their contact details.
              </p>
            </div>

            {/* Country quick-jump pills */}
            <div className="flex flex-wrap gap-2 mb-8">
              {countries.map((c) => (
                <button
                  key={c.name}
                  onClick={() => handleCountryPillClick(c.name)}
                  className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium transition-all duration-200"
                  style={{
                    background: openCountry === c.name ? '#1A5FAB' : 'rgba(15,35,64,0.07)',
                    border: openCountry === c.name ? '1px solid #1A5FAB' : '1px solid rgba(15,35,64,0.15)',
                    color: openCountry === c.name ? '#FFFFFF' : '#0F2340',
                  }}
                >
                  <span>{c.flag}</span>
                  {c.name}
                </button>
              ))}
            </div>

            {/* Accordion list */}
            <div className="space-y-3">
              {countries.map((country) => (
                <CountryAccordion
                  key={country.name}
                  country={country}
                  isOpen={openCountry === country.name}
                  onToggle={() => setOpenCountry(openCountry === country.name ? null : country.name)}
                  searchQuery=""
                  accordionRef={accordionRefs.current[country.name]}
                />
              ))}
            </div>
          </div>
        </section>

        {/* ── Quick Contact Bar ── */}
        <section style={{ background: '#0F2340', borderTop: '1px solid rgba(255,255,255,0.07)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8 py-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                {
                  icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.18 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 5.55 5.55l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" /></svg>,
                  label: 'Call Us', value: '0700 18 986', sub: 'Mon–Fri, 08:30–17:30 EET', href: 'tel:+35970018986',
                },
                {
                  icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>,
                  label: 'Email Us', value: 'info@wto.com', sub: 'Response within 24 hours', href: 'mailto:info@wto.com',
                },
                {
                  icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
                  label: 'Working Hours', value: 'Mon – Fri', sub: '08:30 – 17:00 EET', href: null,
                },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(26,95,171,0.3)', color: '#7EB8F7' }}>
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest mb-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>{item.label}</p>
                    {item.href ? (
                      <a href={item.href} className="text-sm font-semibold block" style={{ color: '#FFFFFF' }}>{item.value}</a>
                    ) : (
                      <p className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>{item.value}</p>
                    )}
                    <p className="text-xs mt-0.5" style={{ color: '#8AAAD4' }}>{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Contact Form + Info ── */}
        <section id="contact-form" className="py-20" style={{ background: '#F4F6F9' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-5 gap-12 items-start">
              {/* Left: info */}
              <div className="lg:col-span-2">
                <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
                <h2 className="text-3xl font-bold mb-4" style={{ color: '#0F2340' }}>
                  Send Us a Message
                </h2>
                <p className="text-base leading-relaxed mb-8" style={{ color: '#3A5070' }}>
                  Have a question about our services or need a custom logistics solution? One of our specialists will get back to you within one business day.
                </p>

                <div className="space-y-4">
                  {[
                    {
                      icon: (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                      ),
                      title: 'Fast Response',
                      desc: 'We reply within 24 business hours',
                    },
                    {
                      icon: (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="2" y1="12" x2="22" y2="12" />
                          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                        </svg>
                      ),
                      title: 'Global Coverage',
                      desc: 'Experts in 16+ countries ready to assist',
                    },
                    {
                      icon: (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                      ),
                      title: 'Confidential',
                      desc: 'Your enquiry is handled with full discretion',
                    },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4 p-4 rounded-xl" style={{ background: '#FFFFFF', border: '1px solid rgba(15,35,64,0.1)' }}>
                      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(26,95,171,0.12)', color: '#1A5FAB' }}>
                        {item.icon}
                      </div>
                      <div>
                        <p className="text-sm font-semibold mb-0.5" style={{ color: '#0F2340' }}>{item.title}</p>
                        <p className="text-xs" style={{ color: '#3A5070' }}>{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 pt-8" style={{ borderTop: '1px solid rgba(15,35,64,0.1)' }}>
                  <p className="text-xs uppercase tracking-widest mb-4" style={{ color: '#6A85A0' }}>Follow Us</p>
                  <div className="flex gap-3">
                    {[
                      { label: 'Facebook', href: 'https://www.facebook.com/WorldTransportOverseas', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> },
                      { label: 'LinkedIn', href: 'https://www.linkedin.com/company/world-transport-overseas', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" /><rect x="2" y="9" width="4" height="12" /><circle cx="4" cy="4" r="2" /></svg> },
                      { label: 'Instagram', href: 'https://www.instagram.com/worldtransportoverseas', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg> },
                    ].map((s) => (
                      <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                        className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200"
                        style={{ border: '1px solid rgba(15,35,64,0.15)', background: '#FFFFFF', color: '#3A5070' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#1A5FAB'; e.currentTarget.style.color = '#FFFFFF'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.color = '#3A5070'; }}
                      >
                        {s.icon}
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right: form */}
              <div className="lg:col-span-3">
                {submitted ? (
                  <div className="rounded-2xl p-10 text-center" style={{ background: '#FFFFFF', border: '1px solid rgba(27,107,138,0.2)' }}>
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'rgba(27,107,138,0.12)' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="#1B6B8A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8"><polyline points="6 9 12 15 18 9" /></svg>
                    </div>
                    <h3 className="text-2xl font-bold mb-3" style={{ color: '#0F2340' }}>Message Sent!</h3>
                    <p className="mb-2" style={{ color: '#3A5070' }}>
                      Thank you, <strong style={{ color: '#0F2340' }}>{form.name}</strong>. We've received your message and will respond to <strong style={{ color: '#1A5FAB' }}>{form.email}</strong> within 24 business hours.
                    </p>
                    <p className="text-sm mb-8" style={{ color: '#6A85A0' }}>For urgent matters, please call us directly at 0700 18 986.</p>
                    <button
                      onClick={() => { setSubmitted(false); setForm({ name: '', company: '', email: '', phone: '', reason: '', message: '' }); }}
                      className="px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 mx-auto"
                      style={{ background: '#1A5FAB', color: '#FFFFFF' }}
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="rounded-2xl p-8" style={{ background: '#FFFFFF', border: '1px solid rgba(15,35,64,0.1)' }}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                      {/* Name */}
                      <div>
                        <label className="block text-xs uppercase tracking-widest mb-2" style={{ color: '#6A85A0' }}>Full Name *</label>
                        <input
                          type="text"
                          placeholder="Your full name"
                          value={form.name}
                          onChange={(e) => update('name', e.target.value)}
                          className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
                          style={{ background: '#F4F6F9', border: errors.name ? '1.5px solid #f87171' : '1.5px solid rgba(15,35,64,0.15)', color: '#0F2340' }}
                        />
                        {errors.name && <p className="text-xs mt-1" style={{ color: '#f87171' }}>{errors.name}</p>}
                      </div>
                      {/* Company */}
                      <div>
                        <label className="block text-xs uppercase tracking-widest mb-2" style={{ color: '#6A85A0' }}>Company</label>
                        <input
                          type="text"
                          placeholder="Your company (optional)"
                          value={form.company}
                          onChange={(e) => update('company', e.target.value)}
                          className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
                          style={{ background: '#F4F6F9', border: '1.5px solid rgba(15,35,64,0.15)', color: '#0F2340' }}
                        />
                      </div>
                      {/* Email */}
                      <div>
                        <label className="block text-xs uppercase tracking-widest mb-2" style={{ color: '#6A85A0' }}>Email Address *</label>
                        <input
                          type="email"
                          placeholder="your@email.com"
                          value={form.email}
                          onChange={(e) => update('email', e.target.value)}
                          className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
                          style={{ background: '#F4F6F9', border: errors.email ? '1.5px solid #f87171' : '1.5px solid rgba(15,35,64,0.15)', color: '#0F2340' }}
                        />
                        {errors.email && <p className="text-xs mt-1" style={{ color: '#f87171' }}>{errors.email}</p>}
                      </div>
                      {/* Phone */}
                      <div>
                        <label className="block text-xs uppercase tracking-widest mb-2" style={{ color: '#6A85A0' }}>Phone</label>
                        <input
                          type="tel"
                          placeholder="+1 234 567 8900"
                          value={form.phone}
                          onChange={(e) => update('phone', e.target.value)}
                          className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
                          style={{ background: '#F4F6F9', border: '1.5px solid rgba(15,35,64,0.15)', color: '#0F2340' }}
                        />
                      </div>
                    </div>

                    {/* Reason */}
                    <div className="mb-5">
                      <label className="block text-xs uppercase tracking-widest mb-2" style={{ color: '#6A85A0' }}>Subject</label>
                      <div className="relative">
                        <select
                          value={form.reason}
                          onChange={(e) => update('reason', e.target.value)}
                          className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all appearance-none"
                          style={{ background: '#F4F6F9', border: '1.5px solid rgba(15,35,64,0.15)', color: form.reason ? '#0F2340' : '#6A85A0' }}
                        >
                          <option value="" disabled>Select a subject…</option>
                          {contactReasons.map((r) => (
                            <option key={r} value={r} style={{ background: '#FFFFFF', color: '#0F2340' }}>{r}</option>
                          ))}
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#1B6B8A' }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Message */}
                    <div className="mb-6">
                      <label className="block text-xs uppercase tracking-widest mb-2" style={{ color: '#6A85A0' }}>Message *</label>
                      <textarea
                        rows={5}
                        placeholder="Tell us about your shipment or inquiry…"
                        value={form.message}
                        onChange={(e) => update('message', e.target.value)}
                        className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all resize-none"
                        style={{ background: '#F4F6F9', border: errors.message ? '1.5px solid #f87171' : '1.5px solid rgba(15,35,64,0.15)', color: '#0F2340' }}
                      />
                    {errors.message && <p className="text-xs mt-1" style={{ color: '#f87171' }}>{errors.message}</p>}
                  </div>

                    {submitError && (
                      <div className="mb-5 px-4 py-3 rounded-xl" style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)' }}>
                        <p className="text-xs" style={{ color: '#dc2626' }}>{submitError}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={sending}
                      className="w-full py-3.5 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2"
                      style={{ background: sending ? 'rgba(26,95,171,0.6)' : '#1A5FAB', color: '#FFFFFF', opacity: sending ? 0.8 : 1 }}
                    >
                      {sending ? (
                        <>
                          <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                          Sending…
                        </>
                      ) : (
                        <>
                          Send Message
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M5 12h14M13 5l7 7 7-7" /></svg>
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ── CTA Banner ── */}
        <section className="py-16" style={{ background: '#0A1535', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-2xl lg:text-3xl font-bold mb-4" style={{ color: '#FFFFFF' }}>
              Need a shipping quote right away?
            </h2>
            <p className="mb-8" style={{ color: '#C8D8F0' }}>
              Use our online quote tool to get a personalised freight estimate in minutes.
            </p>
            <Link
              href="/transport-quote"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold text-sm transition-all duration-200 hover:opacity-90 shadow-lg"
              style={{ background: '#1A5FAB', color: '#FFFFFF' }}
            >
              Get a Free Quote
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M12 5v14M5 12l7 7 7-7" /></svg>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
